import { useState, useEffect, useMemo } from 'react';
import { ExportCSVButton, ExportPDFButton } from '../components/ExportButtons';
import { getOccupancyReport, listAllGolfCourses, listSettings } from '../api';
import ReportFilterBar from '../components/ReportFilterBar';
import { exportCSV, fmtReportDate, fmtCSVDate, fmtCSVFilename } from '../utils/reportExport';
import PrintBanner from '../components/PrintBanner/PrintBanner';
import styles from './OccupancyReportPage.module.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function addDaysISO(iso, n) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}

function fmtDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

function pct(n, d) {
  if (!d || d === 0) return '—';
  return Math.round((n / d) * 100) + '%';
}

function occColor(rate) {
  if (rate >= 0.85) return '#085041';
  if (rate >= 0.70) return '#0F6E56';
  if (rate >= 0.55) return '#1D9E75';
  if (rate >= 0.40) return '#5DCAA5';
  if (rate >= 0.25) return '#9FE1CB';
  return '#C2EBE0';
}

// Inline progress bar component using inline style (dynamic value)
function IBar({ booked, available, color }) {
  if (!available || available === 0) return <span className={styles.dashCell}>—</span>;
  const rate = Math.min(1, booked / available);
  const pctVal = Math.round(rate * 100);
  const barColor = color || occColor(rate);
  return (
    <div className={styles.ibar}>
      <div className={styles.ibarTrack}>
        <div className={styles.ibarFill} style={{ width: `${pctVal}%`, background: barColor }} />
      </div>
      <span className={styles.ibarPct} style={{ color: color ? '#185FA5' : (rate >= 0.55 ? '#085041' : '#6b6b67') }}>
        {pctVal}%
      </span>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function OccupancyReportPage() {
  const today = todayISO();
  const [dateFrom, setDateFrom]   = useState(addDaysISO(today, -29));
  const [dateTo, setDateTo]       = useState(today);
  const [courseId, setCourseId]   = useState('');
  const [courses, setCourses]     = useState([]);

  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [data, setData]           = useState(null);
  const [companyInfo, setCompanyInfo] = useState({ name: '', address: '', website: '', logo: '' });

  useEffect(() => {
    listAllGolfCourses()
      .then(r => setCourses(r.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    listSettings()
      .then(data => {
        const map = {};
        (data.settings || data.items || []).forEach(s => { map[s.key] = s.value; });
        setCompanyInfo({
          name:    map.company_name    || '',
          address: map.company_address || '',
          website: map.company_website || '',
          logo:    map.company_logo    || '',
        });
      })
      .catch(() => {});
  }, []);

  const handleApply = () => {
    if (!dateFrom || !dateTo || dateFrom > dateTo) return;
    setLoading(true);
    setError(null);
    setData(null);
    getOccupancyReport({ dateFrom, dateTo, courseId: courseId || undefined })
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  };

  const kpis = useMemo(() => {
    if (!data?.breakdown?.length) return null;
    const rows = data.breakdown;
    const ttAvail    = rows.reduce((s, r) => s + (r.tee_times_available || 0), 0);
    const ttBooked   = rows.reduce((s, r) => s + (r.tee_times_booked || 0), 0);
    const blocked    = rows.reduce((s, r) => s + (r.blocked_tee_times || 0), 0);
    const players    = rows.reduce((s, r) => s + (r.players || 0), 0);
    const maxPlayers = rows.reduce((s, r) => s + (r.max_players || 0), 0);
    return { ttAvail, ttBooked, blocked, players, maxPlayers, occRate: ttAvail > 0 ? ttBooked / ttAvail : 0 };
  }, [data]);

  return (
    <div className={styles.page}>
      {/* Toolbar — hidden when printing */}
      <div className="no-print">
      <ReportFilterBar
        title="Occupancy rate"
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        courseId={courseId}
        courses={courses}
        onCourseChange={setCourseId}
        onApply={handleApply}
      >
        {data && (
          <>
            <ExportCSVButton
              onClick={() => exportCSV(
                ['Date', 'Available', 'Booked', 'Blocked', 'Occupancy %', 'Players', 'Max players', 'Player utilization %'],
                (data.breakdown || []).map(r => [
                  fmtCSVDate(r.date),
                  r.tee_times_available,
                  r.tee_times_booked,
                  r.blocked_tee_times,
                  r.tee_times_available > 0
                    ? Math.round((r.tee_times_booked / r.tee_times_available) * 100) + '%'
                    : '—',
                  r.players,
                  r.max_players,
                  r.max_players > 0 ? Math.round(((r.players || 0) / r.max_players) * 100) + '%' : '—',
                ]),
                fmtCSVFilename('occupancy_rate', dateFrom, dateTo),
              )}
            />
            <ExportPDFButton onClick={() => window.print()} />
          </>
        )}
      </ReportFilterBar>
      </div>{/* end no-print */}

      {/* Body */}
      <div className={styles.body}>
        <PrintBanner
          companyInfo={companyInfo}
          reportTitle="Occupancy Rate Report"
          dateRange={data ? `${fmtReportDate(dateFrom)} – ${fmtReportDate(dateTo)}` : undefined}
          course={courseId ? (courses.find(c => c.id === Number(courseId))?.name || 'All courses') : 'All courses'}
          generatedOn={new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        />
        {!loading && !data && !error && (
          <div className={styles.emptyState}>Set a date range and click Apply to load the occupancy report.</div>
        )}
        {loading && <div className={styles.emptyState}>Loading...</div>}
        {error && <div className={styles.errorState}>Failed to load data. Please try again.</div>}

        {data && !loading && (
          <>
            {/* KPIs */}
            {kpis && (
              <div className={styles.kpiGrid}>
                <div className={styles.kpi}>
                  <div className={styles.kpiLabel}>Occupancy rate</div>
                  <div className={styles.kpiVal}>{pct(kpis.ttBooked, kpis.ttAvail)}</div>
                  <div className={styles.kpiSub}>{kpis.ttBooked} of {kpis.ttAvail} tee times booked</div>
                </div>
                <div className={styles.kpi}>
                  <div className={styles.kpiLabel}>Player utilization</div>
                  <div className={styles.kpiVal}>{pct(kpis.players, kpis.maxPlayers)}</div>
                  <div className={styles.kpiSub}>{kpis.players.toLocaleString()} of {kpis.maxPlayers.toLocaleString()} player slots used</div>
                </div>
                <div className={styles.kpi}>
                  <div className={styles.kpiLabel}>Total players</div>
                  <div className={styles.kpiVal}>{kpis.players.toLocaleString()}</div>
                  <div className={styles.kpiSub}>Across selected range</div>
                </div>
                <div className={styles.kpi}>
                  <div className={styles.kpiLabel}>Blocked tee times</div>
                  <div className={styles.kpiVal}>{kpis.blocked.toLocaleString()}</div>
                  <div className={styles.kpiSub}>Excluded from occupancy</div>
                </div>
              </div>
            )}

            {/* Day breakdown table */}
            {data.breakdown && data.breakdown.length > 0 ? (
              <div className={styles.card}>
                <div className={styles.cardTitleRow}>
                  <span className={styles.cardTitle}>Day breakdown</span>
                  <div className={styles.legendRow}>
                    <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#085041' }} />≥85% Excellent</span>
                    <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#1D9E75' }} />55–84% Good</span>
                    <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#9FE1CB' }} />&lt;55% Low</span>
                  </div>
                </div>
                <div className={styles.tableWrap}>
                  <table className={styles.occTable}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className={styles.num}>Available</th>
                        <th className={styles.num}>Booked</th>
                        <th className={styles.num}>Blocked</th>
                        <th>Occupancy %</th>
                        <th className={styles.num}>Players</th>
                        <th className={styles.num}>Max players</th>
                        <th>Player utilization</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.breakdown.map((r, i) => (
                        <tr key={i}>
                          <td className={styles.dateCell}>{fmtDate(r.date)}</td>
                          <td className={styles.num}>{r.tee_times_available}</td>
                          <td className={styles.num}>{r.tee_times_booked}</td>
                          <td className={`${styles.num} ${r.blocked_tee_times > 0 ? styles.blockedVal : styles.muted}`}>
                            {r.blocked_tee_times || '—'}
                          </td>
                          <td><IBar booked={r.tee_times_booked} available={r.tee_times_available} /></td>
                          <td className={styles.num}>{(r.players || 0).toLocaleString()}</td>
                          <td className={styles.num}>{(r.max_players || 0).toLocaleString()}</td>
                          <td><IBar booked={r.players || 0} available={r.max_players || 0} color="#85B7EB" /></td>
                        </tr>
                      ))}
                    </tbody>
                    {kpis && (
                      <tfoot>
                        <tr>
                          <td className={styles.totalLabel}>Total</td>
                          <td className={styles.num}>{kpis.ttAvail}</td>
                          <td className={styles.num}>{kpis.ttBooked}</td>
                          <td className={`${styles.num} ${kpis.blocked > 0 ? styles.blockedVal : styles.muted}`}>
                            {kpis.blocked || '—'}
                          </td>
                          <td><IBar booked={kpis.ttBooked} available={kpis.ttAvail} /></td>
                          <td className={styles.num}>{kpis.players.toLocaleString()}</td>
                          <td className={styles.num}>{kpis.maxPlayers.toLocaleString()}</td>
                          <td><IBar booked={kpis.players} available={kpis.maxPlayers} color="#85B7EB" /></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                <p className={styles.note}>
                  Occupancy rate = tee times booked ÷ tee times available (blocked tee times are excluded). Player utilization = players ÷ (tee times available × max players per tee time).
                </p>
              </div>
            ) : (
              <div className={styles.emptyState}>No data for the selected date range.</div>
            )}

          </>
        )}
      </div>
    </div>
  );
}
